
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import styled from "styled-components";
import { DebounceInput } from "react-debounce-input";

import TvsCheckBox from "components/form/CheckBox";
import Arrow from "components/elements/Arrow";

import themeColors from "themes/themeColors";

const Portal = ({ children }) => {
	return createPortal(children, document.body);
};

export const SelectMultipleSalesAgent = ({
	data,
	setSelections,
	selections,
	setIsOpen,
	isOpen,
	width = "100%",
	height = "55px",
	haveSearch = true,
	handleSearch,
	placeholderSearch = "Search"
}: {
	data: {
		id: number;
		name: string;
	}[];
	setSelections: (val: number[]) => void;
	selections: number[];
	setIsOpen: (val: boolean) => void;
	isOpen: boolean;
	width?: string;
	height?: string;
	haveSearch?: boolean;
	handleSearch?: (val: string) => void;
	placeholderSearch?: string;
}) => {
	const widthRef = useRef<any>();
	const [isHovering, setIsHovering] = useState<boolean>(false);
	const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
	const [menuWidth, setMenuWidth] = useState(200);
	const [referenceElement, setReferenceElement] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const { styles, attributes } = usePopper(referenceElement, popperElement, {
		modifiers: [{ name: "arrow", options: { element: null } }],
	});
	const [searchVal, setSearchVal] = useState<string>("");
	const prevSingleSelectionName = useRef<any>();

	useEffect(() => {
		setMenuWidth(
			widthRef?.current?.offsetWidth == 0 ? 200 : widthRef?.current?.offsetWidth,
		);
	}, [widthRef.current]);

  useEffect(() => {
		if (selections.length === 0) {
			setIsAllSelected(false);
		}
	}, [isOpen, selections]);

	const handleMouseEnter = (e) => {
		setIsHovering(true);
	};

	const handleMouseLeave = () => {
		setIsHovering(false);
	};

	const addAllToSelected = () => {
		let allData = [];
		allData = data.map((d) => d.id);
		setSelections([...new Set([...selections].concat(allData))]);
	};

	const getDropdownLabel = () => {
		const numSelected = selections.length;

		if (numSelected === 1) {
			const selectedSalesAgentName = data.find(
				(d) => d.id === selections[0],
			)?.name;

			if (selectedSalesAgentName) {
				prevSingleSelectionName.current = selectedSalesAgentName;
			}
			return selectedSalesAgentName || prevSingleSelectionName.current;
		}

		if (numSelected === 0) {
			return "Select Sales Agent";
		}

		if (numSelected > 1) {
			return `${numSelected} selected`;
		}

		return "Select Branch";
	};

	return (
		<div style={{ width }}>
			<div ref={setReferenceElement}>
				<div
					style={{
						width: "100%",
						display: "flex",
						alignContent: "space-between",
						justifyContent: "space-between",
						alignItems: "center",
						border: selections.length > 0 ? "1px solid #6956ba" : "1px solid #999999",
						height,
						borderRadius: "8px",
						padding: "10px",
						backgroundColor: "white",
					}}
					ref={widthRef}
					aria-label="dropdown"
					tabIndex={0}
					role="button"
					onClick={() => {
						setIsOpen(!isOpen);
					}}>
					<span
						style={{
							paddingRight: "10px",
							fontWeight: "400",
							fontSize: "16px",
							color: selections.length > 0 ? themeColors.Gray333 : themeColors.Gray999,
						}}>
						{getDropdownLabel()}
					</span>
					<Arrow border={2} length={9} color="#666666" rotate={45} />
				</div>
			</div>
			{isOpen && (
				<Portal>
					<div
						style={{
							position: "fixed",
							right: 0,
							left: 0,
							bottom: 0,
							top: 0,
							opacity: 0.5,
						}}
						onMouseDownCapture={() => {
							setIsOpen(false);
						}}
					/>
					<div
						ref={setPopperElement}
						style={{ ...styles.popper, zIndex: 91 }}
						{...attributes.popper}>
						<Container
							style={{
								listStyle: "none",
								boxShadow:
									"0 4px 6px -1px rgb(0 0 0 / 0.1), 0 4px 6px -1px rgb(0 0 0 / 0.1)",
								marginTop: "7px",
								maxHeight: "350px",
								overflowY: "scroll",
								width: menuWidth,
								borderRadius: "4px",
							}}>
							{haveSearch && (
								<WrapSearch>
									<DebounceInput
										debounceTimeout={1000}
										onChange={(event) => {
											handleSearch(event.target.value);
											setSearchVal(event.target.value);
										}}
										style={{ height: "56px", borderRadius: "8px" }}
										placeholder={placeholderSearch}
										className="form-control"
										value={searchVal || ""}
									/>
								</WrapSearch>
							)}
							{data.length > 0 && (
								<li>
									<button
										style={{
											backgroundColor: isHovering ? "#e9edef" : "white",
											fontWeight: isHovering ? "470" : "",
											position: "relative",
											border: "none",
											width: "100%",
											zIndex: "2",
											padding: "16px 8px 16px 16px",
											display: "flex",
											alignContent: "between",
											textAlign: "left",
											alignItems: "center",
											justifyContent: "left",
											borderBottom: "1px solid #e5e3e3",
										}}
										onMouseEnter={() => handleMouseEnter("All")}
										onMouseLeave={handleMouseLeave}
										type="button">
										<TvsCheckBox
											id="branch-dropdown-select-all-branch-check-box"
											handleCheck={(value: string, selected: boolean) => {
												setIsAllSelected(selected);
												if (selected) {
													addAllToSelected();
												} else if (!selected) {
													setSelections([]);
												}
											}}
											value="All"
											selectState={isAllSelected}
										/>
										<span style={{ fontSize: "16px", color: "rgba(40, 50, 55, 1)" }}>
											All
										</span>
									</button>
								</li>
							)}
							{data.map((item, idx) => {
								return (
									<MenuItems
										item={item}
										selections={selections}
										setSelections={setSelections}
										idx={idx}
									/>
								);
							})}
						</Container>
					</div>
				</Portal>
			)}
		</div>
	);
};

const MenuItems = ({
	item,
	selections,
	setSelections,
	idx,
}: {
	item: {
		id: number;
		name: string;
	};
	setSelections: (val: number[]) => void;
	selections: number[];
	idx: number;
}) => {
	const [isHovering, setIsHovering] = useState(false);
	const [open, setOpen] = useState(false);

	const widthRef = useRef<any>();

	const handleMouseEnter = () => {
		setIsHovering(true);
	};

	const handleMouseLeave = () => {
		setIsHovering(false);
	};

	useEffect(() => {
		setOpen(open);
	}, []);

	const inSelection = (item: number) => {
		return selections.includes(item);
	};

	const removeFromList = (item: number) => {
		return selections.filter((e) => e !== item);
	};

	const checkSelectState = () => {
		return inSelection(item.id);
	};

	return (
		<li ref={widthRef}>
			<button
				style={{
					backgroundColor: isHovering ? "#e9edef" : "white",
					fontWeight: isHovering ? "470" : "",
					position: "relative",
					border: "none",
					width: "100%",
					zIndex: "2",
					padding: "16px 8px 16px 16px",
					display: "flex",
					alignContent: "between",
					textAlign: "left",
					justifyContent: "left",
					alignItems: "flex-start",
					borderBottom: "1px solid #e5e3e3",
				}}
				onMouseEnter={() => handleMouseEnter()}
				onMouseLeave={handleMouseLeave}
				type="button">
				<TvsCheckBox
					id={`branch-dropdown-select-option-${idx + 1}-check-box`}
					handleCheck={(value: number, selected: boolean) => {
						if (selected && !inSelection(value)) {
							setSelections([...selections, value]);
						} else if (!selected) {
							setSelections(removeFromList(value));
						}
					}}
					value={item.id}
					selectState={checkSelectState()}
				/>
				<span
					style={{
						fontSize: "16px",
						color: "rgba(40, 50, 55, 1)",
						marginRight: "12px",
					}}>
					{item.name}
				</span>
			</button>
		</li>
	);
};

const Container = styled.ul`
	::-webkit-scrollbar-thumb {
		background-color: darkgray;
		border-radius: 100em;
	}
	::-webkit-scrollbar-track {
		background-color: white;
		border-radius: 100em;
	}
	::-webkit-scrollbar {
		width: 5px;
	}
`;

const WrapSearch = styled.div`
	width: 100%;
	height: 88px;
	padding: 16px 24px 16px 24px;
	background-color: ${themeColors.Gray111};
	position: relative;
	display: flex;
	align-items: center;
	margin: 0;
	&:before {
		background: url(${process.env.PUBLIC_URL}/images/search.png) no-repeat center
			left;
		content: "";
		display: block;
		clear: both;
		width: 20px;
		height: 20px;
		position: absolute;
		top: 34px;
		left: 30px;
	}
	input.form-control {
		padding-left: 30px;
	}
`;